import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import {
  Users,
  Plus,
  Search,
  Filter,
  ChevronRight,
  User,
  Clock,
  CheckCircle,
  AlertCircle,
  FileText,
  TrendingUp,
  Mail
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Skeleton } from '../../components/ui/skeleton';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

const statusConfig = {
  active: { label: 'Active', color: 'bg-emerald-100 text-emerald-700' },
  pending: { label: 'Pending', color: 'bg-amber-100 text-amber-700' },
  completed: { label: 'Completed', color: 'bg-emerald-100 text-emerald-700' },
  archived: { label: 'Archived', color: 'bg-slate-100 text-slate-700' }
};

const ClientCases = () => {
  const navigate = useNavigate();
  const { user, getAuthHeaders } = useAuth();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewCaseModal, setShowNewCaseModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [newCase, setNewCase] = useState({
    client_name: '',
    client_email: '',
    plan: 'diy'
  });
  const [creating, setCreating] = useState(false);

  const isSpecialist = user?.role === 'specialist' || user?.role === 'admin';

  useEffect(() => {
    if (isSpecialist) {
      fetchCases();
    } else {
      setLoading(false);
    }
  }, [isSpecialist]);

  const fetchCases = async () => {
    try {
      const response = await axios.get(`${API}/cases`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setCases(response.data.cases || []);
    } catch (error) {
      console.error('Cases fetch error:', error);
      if (error.response?.status !== 403) {
        toast.error('Failed to load cases');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCase = async () => {
    if (!newCase.client_name || !newCase.client_email) {
      toast.error('Please fill in all required fields');
      return;
    }

    setCreating(true);
    try {
      await axios.post(`${API}/cases`, newCase, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      toast.success('Case created successfully');
      setShowNewCaseModal(false);
      setNewCase({ client_name: '', client_email: '', plan: 'diy' });
      fetchCases();
    } catch (error) {
      console.error('Create case error:', error);
      toast.error(error.response?.data?.detail || 'Failed to create case');
    } finally {
      setCreating(false);
    }
  };

  const filteredCases = cases.filter(c => {
    const matchesSearch = c.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         c.client_email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || c.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (!isSpecialist) {
    return (
      <div className="max-w-2xl mx-auto text-center py-12" data-testid="client-cases-restricted">
        <div className="elite-card p-8">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-amber-100 flex items-center justify-center">
            <Users size={32} className="text-amber-600" />
          </div>
          <h2 className="text-xl font-bold text-stone-900 mb-2">Specialist Access Required</h2>
          <p className="text-slate-500 mb-6">
            Client case management is available for credit repair specialists and administrators.
          </p>
          <Button variant="outline" onClick={() => navigate('/dashboard')}>
            Return to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-6" data-testid="cases-loading">
        <Skeleton className="h-12 w-64" />
        <Skeleton className="h-16" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <Skeleton key={i} className="h-48" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="client-cases">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-stone-900">Client Cases</h1>
          <p className="text-sm text-slate-500">Manage your credit repair clients</p>
        </div>
        <Button onClick={() => setShowNewCaseModal(true)} className="btn-primary">
          <Plus size={18} className="mr-2" />
          New Case
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="flex-1 relative">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search clients..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="archived">Archived</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="elite-card p-4">
          <p className="text-sm text-slate-500">Total Clients</p>
          <p className="text-2xl font-bold text-stone-900">{cases.length}</p>
        </div>
        <div className="elite-card p-4 border-l-4 border-l-emerald-500">
          <p className="text-sm text-slate-500">Active</p>
          <p className="text-2xl font-bold text-emerald-600">
            {cases.filter(c => c.status === 'active').length}
          </p>
        </div>
        <div className="elite-card p-4 border-l-4 border-l-amber-500">
          <p className="text-sm text-slate-500">Pending</p>
          <p className="text-2xl font-bold text-amber-600">
            {cases.filter(c => c.status === 'pending').length}
          </p>
        </div>
        <div className="elite-card p-4 border-l-4 border-l-emerald-500">
          <p className="text-sm text-slate-500">Completed</p>
          <p className="text-2xl font-bold text-emerald-600">
            {cases.filter(c => c.status === 'completed').length}
          </p>
        </div>
      </div>

      {/* Cases Grid */}
      {filteredCases.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredCases.map((clientCase) => {
            const status = statusConfig[clientCase.status] || statusConfig.active;
            return (
              <div 
                key={clientCase.case_id}
                className="elite-card p-5 hover:shadow-lg transition-shadow cursor-pointer"
                data-testid={`case-${clientCase.case_id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                      <User size={20} className="text-slate-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-stone-900">{clientCase.client_name}</p>
                      <p className="text-xs text-slate-500">{clientCase.client_email}</p>
                    </div>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${status.color}`}>
                    {status.label}
                  </span>
                </div>

                <div className="space-y-2 mb-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500">Plan</span>
                    <span className="font-medium text-slate-700 capitalize">{clientCase.plan}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500">Score</span>
                    <span className="font-medium text-slate-700">
                      {clientCase.current_score || '—'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500">Round</span>
                    <span className="font-medium text-slate-700">
                      {clientCase.active_round || 0}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500">Negatives</span>
                    <span className="font-medium text-red-600">
                      {clientCase.negative_count || 0}
                    </span>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                  <span className="text-xs text-slate-500">
                    Created {new Date(clientCase.created_at).toLocaleDateString()}
                  </span>
                  <ChevronRight size={16} className="text-slate-400" />
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="elite-card p-12 text-center">
          <Users size={48} className="mx-auto text-slate-300 mb-4" />
          <h3 className="text-lg font-semibold text-stone-900 mb-2">No cases found</h3>
          <p className="text-slate-500 mb-6">
            {searchTerm || statusFilter !== 'all' 
              ? 'Try adjusting your filters'
              : 'Create your first client case to get started'}
          </p>
          {!searchTerm && statusFilter === 'all' && (
            <Button onClick={() => setShowNewCaseModal(true)}>
              <Plus size={16} className="mr-2" />
              Create First Case
            </Button>
          )}
        </div>
      )}

      {/* New Case Modal */}
      <Dialog open={showNewCaseModal} onOpenChange={setShowNewCaseModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Client Case</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="client_name">Client Name *</Label>
              <Input
                id="client_name"
                value={newCase.client_name}
                onChange={(e) => setNewCase(prev => ({ ...prev, client_name: e.target.value }))}
                placeholder="John Doe"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="client_email">Client Email *</Label>
              <Input
                id="client_email"
                type="email"
                value={newCase.client_email}
                onChange={(e) => setNewCase(prev => ({ ...prev, client_email: e.target.value }))}
                placeholder="john@example.com"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="plan">Service Plan</Label>
              <Select 
                value={newCase.plan} 
                onValueChange={(v) => setNewCase(prev => ({ ...prev, plan: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="diy">DIY ($29/mo)</SelectItem>
                  <SelectItem value="ai_assisted">AI Assisted ($79/mo)</SelectItem>
                  <SelectItem value="premium">Premium ($149/mo)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewCaseModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateCase} disabled={creating} className="btn-primary">
              {creating ? 'Creating...' : 'Create Case'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientCases;
